<template>
  <div
    v-if="
      uploadData &&
        Object.keys(uploadData).length > 0 &&
        uploadData.loadStatus != LoadStatus.loaded
    "
    class="upload-footer-detail"
  >
    <div class="upload-footer-detail__column">
      <div class="upload-footer-detail__progress">
        <v-progress-linear
          :value="Math.round(uploadData.progressPerUpload.totalProgress)"
          color="primary"
        />
      </div>
      <div class="upload-footer-detail__bar d-flex align-center justify-space-between">
        <div class="upload-footer-detail__meta d-flex align-center">
          <div class="upload-footer-detail__title text-truncate">{{ uploadTitle }}</div>
          <div class="mx-2 text-h7 upload-footer-detail__status">
            {{ Math.round(uploadData.progressPerUpload.totalProgress) || 0 }}%
            {{ loadingStateMsg(uploadData.loadStatus) }}
            <span v-if="uploadData.progressPerUpload.totalErrors">{{
              $t('upload.errorCount', { count: uploadData.progressPerUpload.totalErrors })
            }}</span>
          </div>
        </div>
        <div class="upload-footer-detail__actions d-flex align-center">
          <v-btn
            small
            outlined
            class="solvit-crud-outline-btn solvit-crud-outline-btn--info upload-footer-detail__btn"
            @click="showModal"
          >
            {{ $t('common.viewDetails') }}
          </v-btn>
          <v-btn
            v-if="uploadData.loadStatus === LoadStatus.loaded"
            small
            outlined
            class="solvit-crud-outline-btn solvit-crud-outline-btn--danger upload-footer-detail__btn"
            @click="handleClose(BtnType.close)"
          >
            {{ $t('common.close') }}
          </v-btn>
          <v-btn
            v-else
            small
            outlined
            class="solvit-crud-outline-btn solvit-crud-outline-btn--danger upload-footer-detail__btn"
            @click="handleClose(BtnType.cancel)"
          >
            {{ $t('common.cancel') }}
          </v-btn>
        </div>
      </div>
      <v-dialog v-model="dialog" :max-width="modalLayout.SM" v-bind="modalPolicy.P1_DISMISSIBLE">
        <v-card max-height="80vh" class="upload-footer-detail__dialog">
          <div class="upload-footer-detail__section">
            <div class="upload-footer-detail__section-header d-flex align-center justify-space-between">
              <v-card-title class="py-3">{{ $t('upload.errorFileInfo') }}</v-card-title>
              <v-btn
                small
                outlined
                class="solvit-crud-outline-btn mr-2"
                @click="handleErrorTextHidden"
              >
                {{ toggleerror ? $t('common.hideDetails') : $t('common.showDetails') }}
              </v-btn>
            </div>
            <div v-if="toggleerror" class="upload-footer-detail__section-body px-4 pb-3">
              <p
                v-if="!errorFiles.length"
                class="text-body-2 text--secondary mb-0"
              >
                {{ $t('upload.noErrorFiles') }}
              </p>
              <v-list v-else dense>
                <v-list-item
                  v-for="item in errorFiles"
                  :key="`${item.resourceType}-${item.fileKey}`"
                >
                  <v-list-item-content>
                    <v-list-item-title>
                      {{ $t('common.fileName') }}: {{ item.fileKey }}
                    </v-list-item-title>
                    <v-list-item-subtitle>
                      {{ $t('upload.fileError') }}: {{ item.file.error }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </div>
          </div>

          <v-divider />

          <div class="upload-footer-detail__section">
            <div class="upload-footer-detail__section-header d-flex align-center justify-space-between">
              <v-card-title class="py-3">{{ $t('upload.fileDetails') }}</v-card-title>
              <v-btn
                small
                outlined
                class="solvit-crud-outline-btn mr-2"
                @click="handleTextHidden"
              >
                {{ toggle ? $t('common.hideDetails') : $t('common.showDetails') }}
              </v-btn>
            </div>
            <div v-if="toggle" class="upload-footer-detail__section-body px-4 pb-3">
              <p class="text-body-2 mb-2">
                {{
                  $t('upload.batchSummary', {
                    completed: uploadData.progressPerUpload.completedCount || 0,
                    total: uploadData.progressPerUpload.totalFileCount || 0
                  })
                }}
              </p>
              <p
                v-if="!visibleDetailFiles.length"
                class="text-body-2 text--secondary mb-0"
              >
                {{ $t('upload.noActiveFiles') }}
              </p>
              <v-list v-else dense class="upload-footer-detail__file-list">
                <v-list-item
                  v-for="item in visibleDetailFiles"
                  :key="`${item.resourceType}-${item.fileKey}`"
                >
                  <v-list-item-content>
                    <v-list-item-title>{{ $t('common.fileName') }}: {{ item.fileKey }}</v-list-item-title>
                    <v-list-item-subtitle v-if="item.file.error">
                      {{ $t('upload.fileError') }}: {{ item.file.error }}
                    </v-list-item-subtitle>
                    <v-list-item-subtitle v-else>
                      {{ $t('upload.progressPercent', { progress: item.file.progress }) }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </div>
          </div>

          <v-card-actions class="justify-end px-4 pb-4">
            <v-btn outlined class="solvit-crud-outline-btn" @click="dialog = false">
              {{ $t('common.close') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { LoadStatus } from '../../store/modules/upload/upload-file.store';
import { MODAL_MAX_WIDTH, MODAL_CLOSE_POLICY } from '@/constants/modalLayout';

const BtnType = {
  close: 'close',
  cancel: 'cancel'
};

function isInFlightOrFailed(file) {
  if (!file) {
    return false;
  }
  if (file.error) {
    return true;
  }
  const progress = Number(file.progress);
  return Number.isFinite(progress) && progress < 100;
}

export default {
  name: 'UploadFooterDetail',
  props: {
    uploadId: {
      type: String,
      required: true
    }
  },
  data: () => ({
    modalLayout: MODAL_MAX_WIDTH,
    modalPolicy: MODAL_CLOSE_POLICY,
    dialog: false,
    LoadStatus,
    toggle: false,
    toggleerror: false,
    BtnType
  }),
  computed: {
    ...mapGetters({
      uploads: 'uploadfile/uploads'
    }),

    uploadTitle() {
      const lastIndex = this.uploadId.lastIndexOf('_');
      const uploadTime = this.uploadId.slice(0, lastIndex);
      const truncateReportName = (reportName) => {
        if (reportName.length > 10) {
          return `${reportName.slice(0, 5)}...${reportName.slice(-5)}`;
        }
        return reportName;
      };
      return `${truncateReportName(this.uploadData.reportName)} ${uploadTime} ${
        this.uploadData.resourceType
      }`;
    },
    uploadData() {
      return this.uploads(this.$props.uploadId);
    },
    uploadFileEntries() {
      const entries = [];
      const progressByResource = this.uploadData?.uploadProgress || {};

      Object.entries(progressByResource).forEach(([resourceType, files]) => {
        Object.entries(files || {}).forEach(([fileKey, file]) => {
          entries.push({ resourceType, fileKey, file });
        });
      });

      return entries;
    },
    errorFiles() {
      return this.uploadFileEntries.filter(({ file }) => Boolean(file?.error));
    },
    visibleDetailFiles() {
      return this.uploadFileEntries.filter(({ file }) => isInFlightOrFailed(file));
    },
    hasUploadErrors() {
      return this.errorFiles.length > 0;
    }
  },
  methods: {
    ...mapActions({
      resetUpload: 'uploadfile/resetUpload',
      setLoadStatus: 'uploadfile/setLoadStatus'
    }),
    handleTextHidden() {
      this.toggle = !this.toggle;
    },
    handleErrorTextHidden() {
      this.toggleerror = !this.toggleerror;
    },
    handleClose(btnType) {
      if (
        btnType === BtnType.close ||
        (btnType === BtnType.cancel &&
          confirm(this.$t('upload.confirmCancel')))
      ) {
        this.resetUpload(this.$props.uploadId);
        this.setLoadStatus({
          uploadId: this.$props.uploadId,
          status: LoadStatus.initial
        });
        this.dialog = false;
      }
    },
    showModal() {
      this.toggleerror = this.hasUploadErrors;
      this.toggle = false;
      this.dialog = true;
    },
    loadingStateMsg(status) {
      switch (status) {
      case LoadStatus.loaded:
        return this.$t('upload.status.complete');
      case LoadStatus.loading:
        return this.$t('upload.status.uploading');
      case LoadStatus.error:
        return this.$t('upload.status.error');
      case LoadStatus.retry:
        return this.$t('upload.status.retrying');
      default:
        return this.$t('upload.status.waiting');
      }
    }
  }
};
</script>

<style scoped>
.upload-footer-detail {
  flex: 0 0 auto;
}

.upload-footer-detail__column {
  display: flex;
  width: 100%;
  flex-direction: column;
}

.upload-footer-detail__progress {
  height: 4px;
}

.upload-footer-detail__bar {
  background-color: color-mix(in srgb, var(--v-text-base) 22%, var(--v-surface-base));
  border-radius: 5px;
  padding: 6px 8px;
  gap: 8px;
  min-height: 48px;
}

.upload-footer-detail__meta {
  min-width: 0;
  flex: 1 1 auto;
}

.upload-footer-detail__title {
  max-width: 240px;
}

.upload-footer-detail__status {
  min-width: 130px;
  white-space: nowrap;
}

.upload-footer-detail__actions {
  flex: 0 0 auto;
  gap: 6px;
}

.upload-footer-detail__btn {
  min-width: 76px;
}

.upload-footer-detail__dialog {
  padding: 0 10px;
}

.upload-footer-detail__file-list {
  max-height: 40vh;
  overflow-y: auto;
}
</style>
